import { useEffect, useState } from "react";
import { fetchResearchEvidence, type ResearchEvidenceDto } from "../api";
import { SimpleMarkdown } from "../components/SimpleMarkdown";
import { useSeoTags } from "../useSeoTags";
import { IndexDirectionArticlePage } from "./IndexDirectionArticlePage";
import { CorroborationArticlePage } from "./CorroborationArticlePage";
import { TurnoverArticlePage } from "./TurnoverArticlePage";
import { Momentum5dArticlePage } from "./Momentum5dArticlePage";
import { FiftyTwoWeekHighArticlePage } from "./FiftyTwoWeekHighArticlePage";
import { Phase3SweepArticlePage } from "./Phase3SweepArticlePage";
import { FpiCyclicityArticlePage } from "./FpiCyclicityArticlePage";
import { TaSynthesisArticlePage } from "./TaSynthesisArticlePage";
import "../marketMyths.css";

// These 8 static evidence docs render as bespoke, humanized Medium-style
// components (6 signed off 2026-10-28, fpi-cyclicity and ta-synthesis
// added 2026-10-25 as their own standalone pages -- see Track B notes
// below) instead of through the generic EvidenceReader/SimpleMarkdown
// path -- same URL pattern (/market-myths?evidence=SLUG), different page
// layout. EvidenceReader itself is left defined below (unreachable for
// these 8 slugs now, since every non-evidenceHref myth in MYTHS maps to
// one of these components) rather than deleted in this same commit -- a
// separate cleanup, not part of this content flip.
const TEMPLATED_EVIDENCE: Record<string, (props: { onBack: () => void }) => React.ReactElement> = {
  "index-direction": IndexDirectionArticlePage,
  "corroboration": CorroborationArticlePage,
  "turnover": TurnoverArticlePage,
  "momentum-5d": Momentum5dArticlePage,
  "fifty-two-week-high": FiftyTwoWeekHighArticlePage,
  "phase3-sweep": Phase3SweepArticlePage,
  "fpi-cyclicity": FpiCyclicityArticlePage,
  "ta-synthesis": TaSynthesisArticlePage,
};

type Verdict = "bust" | "conf" | "dep" | "unp";

interface Myth {
  slug: string;
  claim: string;
  verdict: Verdict;
  verdictLabel: string;
  why: React.ReactNode;
  strength: number; // 1-5
  preregistered: string;
  // Full page navigation instead of the in-app ?evidence= markdown reader
  // -- for a myth whose evidence is an interactive LIVING article
  // (research_cache-backed), not a static writeup. Undefined (the
  // default) keeps the normal openEvidence() behaviour.
  evidenceHref?: string;
  // Overrides the default "explore the live data" label on an
  // evidenceHref card. Currently unused: the one case that needed it
  // (FPI-cyclicity, which used to cross-link into ANOTHER myth's slug)
  // was resolved 2026-10-25 by giving fpi-cyclicity its own standalone
  // page at its own slug instead -- left in the type for the next myth
  // that genuinely needs an evidenceHref pointing somewhere non-default.
  evidenceLabel?: string;
}

// SCOPE. As of the 2026-10-25 batch, 9 of the 10 original candidate myths
// reviewed have a real, publicly-linkable evidence trail: the original two
// (index-direction, sue-pead) plus five new static docs under
// docs/research/ (corroboration, turnover, momentum-at-5d, 52-week-high,
// the Phase-3 price/volume sweep) -- every number in each sourced to a
// committed artifact (feature_library or a persisted JSON result file),
// per the verdict-accuracy gate review this batch went through. FPI-
// cyclicity originally cross-linked the existing index-direction writeup's
// §7.2 (the FPI-flow-vs-rotation investigation first published there);
// promoted to its own standalone article (Track B, 2026-10-25) built
// ENTIRELY from our own derived statistics (correlations, p-values, test
// counts) -- zero raw NSDL flow figures, per NSDL's own no-redistribution
// terms (confirmed live before building). An 11th card, TA synthesis
// (Track B, 2026-10-25), is a deliberate umbrella/index piece rather than
// a new investigation -- it classifies every popular technical-analysis
// tool against the live feature_library table, cross-links the four
// already-public cards instead of restating their numbers, first-surfaces
// several already-blessed-but-never-public features (RSI, Stochastic,
// MACD, Bollinger, SMA ratios, the golden/death-cross regime, ADX, ATR,
// OBV), and runs exactly one new gap-fill (an RSI/Stochastic decile cut,
// reusing the existing decile-chart machinery -- no new sweep). Ensembles was reviewed and DROPPED, not
// published: no dedicated ensemble-vs-single-model backtest exists in
// this codebase, only production blending infrastructure -- "untested"
// is not the same claim as "busted," and this page only publishes tested
// findings. A smaller, fully-backed page beats a fuller one with dead
// links (the project's own anti-drift guard, userstories_market_myths.md
// US-B3) -- that discipline is why Ensembles stays off, not an oversight.
const MYTHS: Myth[] = [
  {
    slug: "index-direction",
    claim: "You can forecast which way an NSE index will move over the next few weeks.",
    verdict: "bust",
    verdictLabel: "BUSTED",
    why: (
      <>
        Four separate attempts at NSE indices — a blessed technical/macro sweep, a wider search including flow
        and volatility regimes, calendar structure, and cross-sectional rotation — at horizons from 5 to 30
        trading days. None found a directional edge; only the <b>size</b> of a move holds up, never which way it
        goes. We went further and audited <b>our own earlier indices model</b>, which once claimed 62.7% average
        accuracy across its own 16 indices — once walk-forward testing and a real base-rate comparison were
        applied, it came in <b>worse than simply guessing the majority class</b> on 11 of those 16. The same
        method finds real signal in individual stocks, so this is indices being genuinely efficient at this
        level, not our tools missing something.
      </>
    ),
    strength: 4,
    preregistered: "methodology fixed before testing (not formally pre-registered) · Sep 2026",
  },
  {
    slug: "sue-pead",
    claim: "A stock keeps drifting in the direction of an earnings surprise for weeks afterward.",
    verdict: "dep",
    verdictLabel: "IT DEPENDS",
    why: (
      <>
        Tested on real earnings-surprise history, pre-registered before we looked at a result — the entry point
        excludes the announcement-day jump itself, significance comes from HAC/Newey-West, and every bucket
        carries a matched placebo. At most <b>one or two of 10 sectors</b> clear our significance bar in any
        given month, concentrated in <b>smaller/younger names</b>, and only on that <b>60-day horizon — never
        immediately</b>. Leave-one-out testing shows even the surviving sector doesn't rest on robust evidence —
        drop any single reporting quarter and it no longer clears correction. The same discipline caught and
        rejected a result that looked promising at first glance in Financial Services. This is a faint,
        unstable finding, <b>not a tradeable signal</b>: it hasn't been checked against real trading costs, and
        the underlying data skews toward larger, surviving companies.
      </>
    ),
    strength: 3,
    preregistered: "pre-registered · Oct 2026",
    evidenceHref: "/sue-pead",
  },
  {
    slug: "index-rotation",
    claim: "NSE sector indices take predictable turns at the top of the rankings.",
    verdict: "bust",
    verdictLabel: "BUSTED",
    why: (
      <>
        Every week, NSE's 15 major sector indices get re-ranked 1–15 by return — NIFTY and SENSEX sit out as
        broad benchmarks, not sectors. Four separate questions, each tested against a shuffled-week baseline:
        does a leader stay near the top, does last week's best or worst revert, do rank-quartiles show real
        transition structure, and does any rule beat simply holding the equal-weight basket out of sample.{" "}
        <b>All four land inside what random reshuffling alone produces.</b> This page runs the test{" "}
        <b>live, recomputed weekly</b>, with the real rank history laid out as a sortable table — pick a sector,
        highlight its #1 weeks, see the real gaps between them — so you can look for the pattern yourself before
        finding out why it isn't there.
      </>
    ),
    strength: 4,
    preregistered: "methodology fixed before testing (not formally pre-registered) · Oct 2026",
    evidenceHref: "/index-rotation",
  },
  {
    slug: "macro-factors",
    claim: "Macro factors — oil, the dollar, interest rates, volatility — predict where Indian indices go next.",
    verdict: "bust",
    verdictLabel: "BUSTED",
    why: (
      <>
        Tested the dollar (DXY), USD/INR, the US 10-year yield, US and India VIX, and major overseas markets
        (S&P 500, Nikkei, Hang Seng) against 7 NSE indices — the same 7-index universe and method our
        index-direction work's Investigation 2 used. Oil and India's own interest rate were never part of the
        tested pool — oil wasn't in the original feature set, and India's 10-year yield has no usable data in
        our database. <b>The factors genuinely move with the indices</b> — India VIX's daily swings correlate as
        strongly as -0.60 with NIFTY same-day. <b>None of it forecasts</b>, though: only 3 of 70 index-factor
        pairs nominally cleared significance, and zero survived walk-forward replication.{" "}
        <b>Correlation isn't prediction.</b>
      </>
    ),
    strength: 4,
    preregistered: "methodology fixed before testing (not formally pre-registered) · Oct 2026",
    evidenceHref: "/macro-factors",
  },
  {
    slug: "corroboration",
    claim: "When several independent models agree on a stock, that agreement itself makes the prediction more reliable.",
    verdict: "bust",
    verdictLabel: "BUSTED",
    why: (
      <>
        Measured three separate times — once informally, once against a historical replay, once against live
        production, our largest and most current sample. <b>The three reads disagree not just on magnitude but
        on direction</b> — which bucket does better flips between runs. A real effect doesn't flip sign between
        re-measurements of the same thing; that instability is the evidence. This was an exploratory diagnostic,
        not a pre-registered test with placebo controls — a real limitation.
      </>
    ),
    strength: 2,
    preregistered: "exploratory diagnostic, not pre-registered · re-run Oct 2026",
  },
  {
    slug: "turnover",
    claim: "Unusually high trading turnover — either persistently or as a sudden spike — predicts a stock's next move.",
    verdict: "dep",
    verdictLabel: "IT DEPENDS",
    why: (
      <>
        The <b>level</b> of turnover is a blessed production feature with a real, significant pooled correlation
        — IC +0.041, t=13.8 — but look inside the average and it's shaky: the decile pattern isn't clean, and
        the sign flips negative in three of the four cap-tiers. A small pooled positive that doesn't hold across
        segments or deciles is one weak input, not an edge. A sudden turnover <b>spike</b> is a cleaner no: its
        raw correlation and its leftover-after-controlling-for-everything-else correlation point in opposite
        directions, which disqualifies it regardless of how significant either number looks alone.
      </>
    ),
    strength: 3,
    preregistered: "pre-registered (spike leg) / production blessing record (level leg) · Oct 2026",
  },
  {
    slug: "momentum-5d",
    claim: "A stock that's been rising strongly over the past week will keep rising.",
    verdict: "bust",
    verdictLabel: "BUSTED",
    why: (
      <>
        It reverses instead. 5-day rate of change carries a pooled correlation of <b>−0.037</b> with forward
        returns — t=−16.2, one of the largest, most consistent effects in our feature library — with the same
        negative sign in every cap-tier segment, and <b>stable in sign across sub-periods</b> too, the same
        stability record the 52-week-high effect on this page carries. Stocks that rose fastest over the last 5
        days tend to give a little of it back next. It ranks reliably, but the effect itself is small, not a
        large, obviously tradeable edge on its own. A separate, longer-horizon momentum feature is shown only as
        context — it carries its own distinct, unstable, MARGINAL record, sign flipping by regime, never proof
        that momentum works elsewhere.
      </>
    ),
    strength: 4,
    preregistered: "production blessing record · blessed 2026-08-08",
  },
  {
    slug: "fifty-two-week-high",
    claim: "Stocks trading near their 52-week high keep outperforming.",
    verdict: "conf",
    verdictLabel: "CONFIRMED",
    why: (
      <>
        A real, blessed, segment-consistent effect — pooled IC +0.043, t=13.4, across 2,222 trading days,
        positive in every cap-tier tested, with a <b>stable sign across sub-periods</b> — the same stability
        record the 5-day effect on this page carries, unlike the separate, longer-horizon momentum feature
        discussed there, which our own records flag MARGINAL and unstable. The decile-sorted pattern isn't
        perfectly clean, though — monotonicity −0.18 — confirmed, with a caveat, not a flawless result.
      </>
    ),
    strength: 4,
    preregistered: "production blessing record · blessed 2026-08-08",
  },
  {
    slug: "phase3-sweep",
    claim: "Price/volume patterns like idiosyncratic volatility, beta, liquidity cost, and calendar seasonality can sharpen our stock predictions.",
    verdict: "bust",
    verdictLabel: "BUSTED",
    why: (
      <>
        A pre-registered sweep of 8 candidates — two (turnover, 52-week-high) have their own cards; this one
        covers the other six — and <b>none shipped, for three different reasons</b>, not one blanket "didn't
        work." `idio_vol` (t=−12.6) and `max_1m` (t=−11.6) are among the strongest raw numbers in this project's
        research, rejected anyway: once you remove what's already captured by production features, `idio_vol`
        restates existing volatility outright (ρ=0.71) and `max_1m` even more so (ρ=0.80), its leftover signal
        flipping sign entirely. `beta_252` and `amihud_21` are real but far more modest, adding nothing once
        that same information is removed. <b>A feature can be overwhelmingly significant and still add
        nothing</b>, because it only restates what the model already has. `mom_6m` is a clean null — no real
        correlation, sign disagreeing between the two test halves. `season_month` is the most interesting: it
        nominally <b>clears every gate</b> here, but a separate follow-up found this project's own gates carry a
        small, statistically real false-positive floor — corrected for it, and checked for stability over time,
        the effect is too small and unproven to deploy. <b>Statistically real and still not shipped</b> — a
        different lesson from the other five.
      </>
    ),
    strength: 4,
    preregistered: "pre-registered · 445 trading dates",
  },
  {
    slug: "fpi-cyclicity",
    claim: "Foreign portfolio investment (FPI) flows move in predictable cycles that forecast which sector leads next.",
    verdict: "bust",
    verdictLabel: "BUSTED",
    why: (
      <>
        Part of our index-direction investigation: FPI sector flow tested against next-period sector rank
        change, 26 tests across 13 sectors and 2 horizons, against a Bonferroni threshold of α=0.0019.{" "}
        <b>Zero of 26 survive.</b> The best nominal results — NIFTY Infra +0.154, p=0.018; Bank Nifty +0.192,
        p=0.023 — don't come close to the bar a 26-test family requires.
      </>
    ),
    strength: 4,
    preregistered: "methodology fixed before testing (not formally pre-registered) · Sep 2026",
  },
  {
    slug: "ta-synthesis",
    claim: "Technical analysis is just chart-reading superstition — none of it holds up.",
    verdict: "dep",
    verdictLabel: "IT DEPENDS",
    why: (
      <>
        Not one claim — eighteen. Tested the same way as everything else on this page: RSI, Stochastic, MACD,
        Bollinger Bands, moving-average ratios, the golden/death cross, ADX, ATR and OBV are all <b>blessed,
        real, modest cross-sectional signals</b> already running in production — most shown publicly for the
        first time here. Four other indicators have their own dedicated pages. Support/resistance and classic
        chart patterns are honestly excluded, not testable with what this pipeline can define.
      </>
    ),
    strength: 3,
    preregistered: "synthesis of existing blessed features + feature_library · Oct 2026",
  },
];

// Real commit dates (git log --follow), not guessed -- used for the
// evidence doc's JSON-LD datePublished/dateModified. Verified unambiguous
// for both docs before adding: datePublished = the doc's first commit
// (when the finding was originally written up); dateModified = its most
// recent substantive edit (the Market Myths public-readiness rework).
// Omitted entirely for any slug not listed here, rather than guessed.
const EVIDENCE_DATES: Record<string, { datePublished: string; dateModified?: string }> = {
  "index-direction": { datePublished: "2026-09-12", dateModified: "2026-10-24" },
  "sue-pead": { datePublished: "2026-10-24" }, // written and last substantively edited the same day
  // The 5 new 2026-10-25 batch docs -- written and last substantively
  // edited the same day (git log --follow confirmed for each).
  "corroboration": { datePublished: "2026-10-25" },
  "turnover": { datePublished: "2026-10-25" },
  "momentum-5d": { datePublished: "2026-10-25" },
  "fifty-two-week-high": { datePublished: "2026-10-25" },
  "phase3-sweep": { datePublished: "2026-10-25" },
};

const VERDICT_META: Record<Verdict, { label: string; className: string; dot: string }> = {
  bust: { label: "Busted — evidence says no", className: "mm-v-bust", dot: "mm-dot-bust" },
  conf: { label: "Confirmed — holds up", className: "mm-v-conf", dot: "mm-dot-conf" },
  dep: { label: "It depends — only conditionally", className: "mm-v-dep", dot: "mm-dot-dep" },
  unp: { label: "Unproven — not enough evidence", className: "mm-v-unp", dot: "mm-dot-unp" },
};

function StrengthDots({ n }: { n: number }) {
  return (
    <div className="mm-dots">
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={i <= n ? "on" : ""} />
      ))}
    </div>
  );
}

// The page already renders data.title as the page's own <h1>; the source
// markdown ALSO opens with its own "# Title" line (the doc's real,
// usually-longer title). Left in, SimpleMarkdown would render that as a
// second, near-duplicate heading right below the first -- stripped here
// rather than ever rendered twice (found by an actual pixel-check, not by
// reading the code).
function stripLeadingH1(markdown: string): string {
  const lines = markdown.split("\n");
  if (lines[0]?.startsWith("# ")) {
    let i = 1;
    while (i < lines.length && lines[i].trim() === "") i++;
    return lines.slice(i).join("\n");
  }
  return markdown;
}

function EvidenceReader({ slug, onBack }: { slug: string; onBack: () => void }) {
  const [data, setData] = useState<ResearchEvidenceDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const myth = MYTHS.find((m) => m.slug === slug);
  const canonical = `https://tickerpicker.in/market-myths?evidence=${slug}`;

  useEffect(() => {
    setData(null);
    setError(null);
    fetchResearchEvidence(slug)
      .then(setData)
      .catch(() => setError("Could not load this evidence doc."));
  }, [slug]);

  // Per-doc SEO: only applied once the real title has loaded (data.title),
  // so this never ships a placeholder/loading string as a page's indexed
  // title or description. Falls back to the Market Myths page's own tags
  // (set by the parent's useSeoTags call) until then.
  useSeoTags({
    title: data ? `${data.title} — Tickerpicker` : "Market Myths: Tested — Tickerpicker",
    description: data && myth
      ? `The full empirical evidence behind the Market Myths verdict on: "${myth.claim}" — every test, every number, pre-registered methodology and all, including the parts that failed.`
      : "Market Myths: Tested — pre-registered, placebo-controlled tests of common market claims, on Tickerpicker.",
    canonical,
    ogType: "article",
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: data.title,
          description: myth
            ? `The full empirical evidence behind the Market Myths verdict on: "${myth.claim}"`
            : undefined,
          url: canonical,
          publisher: { "@type": "Organization", name: "Tickerpicker" },
          isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
          // Real commit dates only (EVIDENCE_DATES above) -- never a
          // fabricated or "now()" placeholder. Omitted entirely for a
          // slug not in that map rather than guessed.
          ...(EVIDENCE_DATES[slug]?.datePublished ? { datePublished: EVIDENCE_DATES[slug].datePublished } : {}),
          ...(EVIDENCE_DATES[slug]?.dateModified ? { dateModified: EVIDENCE_DATES[slug].dateModified } : {}),
        }
      : undefined,
  });

  return (
    <div className="mm-shell">
      <nav className="mm-nav">
        <a href="/" className="mm-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
          <a href="/">Home</a>
          <span className="mm-crumb-sep">/</span>
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </nav>
      {error && <div className="mm-error">{error}</div>}
      {!data && !error && <div className="mm-loading">Loading…</div>}
      {data && (
        <article className="mm-evidence">
          <div className="mm-kick">Research Log evidence</div>
          <h1 className="mm-evidence-title">{data.title}</h1>
          <SimpleMarkdown markdown={stripLeadingH1(data.markdown)} />
          <div className="mm-evid-footnav">
            <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
          </div>
        </article>
      )}
    </div>
  );
}

export function MarketMythsPage() {
  const [evidenceSlug, setEvidenceSlug] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("evidence");
  });

  // Signed off public 2026-10-26, after two review rounds: a public-page
  // framing pass (nav/CTA copy) and a deeper evidence-doc rework (plain-
  // language summaries, "V8" -> "our own earlier indices model", local
  // paths and citation scaffolding removed, a quarters/years unit error
  // fixed). The noindex-on-mount guard used during both review rounds is
  // removed; this page is now indexable like the rest of the public site.
  useSeoTags({
    enabled: !evidenceSlug,
    title: "Market Myths: Tested — Tickerpicker",
    description: "We tested the things everyone “knows” about Indian markets, using the same " +
      "pre-registered, placebo-controlled methodology as our own trading signals — and we publish what " +
      "didn’t work, not just what did.",
    canonical: "https://tickerpicker.in/market-myths",
    ogType: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Market Myths: Tested",
      url: "https://tickerpicker.in/market-myths",
      description: "Pre-registered, placebo-controlled tests of common claims about Indian equity markets, " +
        "including the ones that didn't hold up.",
      publisher: { "@type": "Organization", name: "Tickerpicker" },
    },
  });

  // Browser back/forward changes the URL without re-running React state --
  // without this, pressing Back after opening an evidence doc leaves the
  // reader view stuck on screen while the address bar shows the grid URL
  // (caught by an actual browser pixel-check, not by reading the code).
  useEffect(() => {
    function onPopState() {
      const params = new URLSearchParams(window.location.search);
      setEvidenceSlug(params.get("evidence"));
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function openEvidence(slug: string) {
    setEvidenceSlug(slug);
    window.history.pushState({}, "", `/market-myths?evidence=${slug}`);
  }
  function closeEvidence() {
    setEvidenceSlug(null);
    window.history.pushState({}, "", "/market-myths");
  }

  if (evidenceSlug) {
    const Templated = TEMPLATED_EVIDENCE[evidenceSlug];
    if (Templated) return <Templated onBack={closeEvidence} />;
    return <EvidenceReader slug={evidenceSlug} onBack={closeEvidence} />;
  }

  return (
    <div className="mm-shell">
      <nav className="mm-nav">
        <a href="/" className="mm-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
          <a href="/">Home</a>
          <span className="mm-crumb-sep">/</span>
          <a href="/working-paper">Working Paper</a>
        </div>
      </nav>

      <div className="mm-hero">
        <div className="mm-kick">Market Myths: Tested</div>
        <h1>We checked the things everyone &ldquo;knows.&rdquo;</h1>
        <p>
          Most market wisdom is never actually tested. We run each claim through the same rigorous,
          placebo-controlled validation we use on our own signals, with the test fixed before we see the result —
          and <b>we publish what didn&apos;t work</b>, not just what did.
        </p>
        <div className="mm-manifesto">A tested <b>null</b> is a finding. &ldquo;We can&apos;t tell yet&rdquo; is an honest answer.</div>
      </div>

      <div className="mm-legend">
        {(["bust", "conf", "dep", "unp"] as Verdict[]).map((v) => (
          <span key={v} className="mm-lchip"><span className={`mm-vdot ${VERDICT_META[v].dot}`} />{VERDICT_META[v].label}</span>
        ))}
      </div>

      <div className="mm-grid">
        {MYTHS.map((m) => {
          const meta = VERDICT_META[m.verdict];
          return (
            <div key={m.slug} className={`mm-myth ${meta.className}`}>
              <div className="mm-claimlbl">The myth</div>
              <div className="mm-claim">&ldquo;{m.claim}&rdquo;</div>
              <span className={`mm-verdict ${meta.className}`}><span className={`mm-vdot ${meta.dot}`} />{m.verdictLabel}</span>
              <div className="mm-expl">{m.why}</div>
              <div className="mm-strength">
                <span className="l">Evidence</span>
                <StrengthDots n={m.strength} />
              </div>
              <div className="mm-foot">
                <span className="mm-prereg">{m.preregistered}</span>
                {m.evidenceHref ? (
                  // A living, interactive article is a full separate page,
                  // not the in-app ?evidence= markdown reader -- plain
                  // navigation, nothing intercepted.
                  <a className="mm-evid" href={m.evidenceHref}>
                    {m.evidenceLabel ?? "explore the live data →"}
                  </a>
                ) : (
                  <a
                    className="mm-evid"
                    href={`/market-myths?evidence=${m.slug}`}
                    onClick={(e) => {
                      // Real href (crawlable, right-click-able, opens in a new
                      // tab) but intercepted for the smooth in-app transition
                      // on a normal click -- a <button onClick> here would
                      // work for users but gives a crawler nothing to follow.
                      e.preventDefault();
                      openEvidence(m.slug);
                    }}
                  >
                    see the evidence &rarr;
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mm-footnote">
        Every verdict links to the full technical evidence in our internal Research Log. For each myth, the test
        and its pass/fail criteria were fixed <b>before</b> we looked at the result; where a test was{" "}
        <b>formally pre-registered</b>, its stamp says so. Only myths with a persisted, linkable writeup are
        published here; several other internally-tested claims are held back until their own evidence is written
        up this way.
        <br />
        TickerPicker is a personal, non-commercial research project. Nothing here is investment advice.
      </div>
    </div>
  );
}
